import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WIDGET_MANAGEMENT_CRUD } from '../../Constants/ApiDefinations/index'
import { DATA_ENGINES } from '../../Constants/ManageWidget'
import { IManageWidgetSlice } from '../../Models/ManageWidget'
import mainApiService from '../../Services'
import { IPinnedItemDetailInRedux } from '../../Models/DataCategoryUtils'

const initialState: IManageWidgetSlice = {
  widgetDefinitionId: '',
  widgetDetailId: '',
  widgetDefinitionDetails: {},
  widgetDetails: {},
  dataEngine: {
    key: '',
    title: '',
    selectedAssets: [],
    selectedAssetDetails: [],
    newlySelectedAssets: [],
  },
  isDirty: false,
  isPinAssetDirty: false,
  manualPinnedItems: {
    ids: [],
    position: [],
    items: [],
  },
}

const manageWidgetSlice = createSlice({
  name: 'manageWidget',
  initialState,
  reducers: {
    setWidgetDefinitionId: (state, action: PayloadAction<string>) => {
      state.widgetDefinitionId = action.payload
    },
    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload
    },
    setIsPinAssetDirty: (state, action: PayloadAction<boolean>) => {
      state.isPinAssetDirty = action.payload
    },
    setWidgetDetailId: (state, action: PayloadAction<string>) => {
      state.widgetDetailId = action.payload
    },
    setWidgetDefinitionDetails: (state, action: PayloadAction<{ type: string; value: any }>) => {
      const { type, value } = action.payload
      if (type === 'add') {
        state.widgetDefinitionDetails = { ...value }
      } else {
        state.widgetDefinitionDetails = {}
      }
    },
    setWidgetDetails: (state, action: PayloadAction<{ type: string; value: any }>) => {
      const { type, value } = action.payload
      if (type === 'add') {
        state.widgetDetails = { ...value }
      } else {
        state.widgetDetails = {}
      }
    },
    setSelectedAssetDetails: (state, action: PayloadAction<any[]>) => {
      const existingIds = new Set(
        state.dataEngine.selectedAssetDetails?.map((asset) => getIDKey(asset)),
      )
      // Create updatedAssets array by retaining the order of action.payload
      const updatedAssets = action.payload.map((asset) => {
        const assetId = getIDKey(asset)
        // If asset already exists, use the existing object; otherwise, use the new asset
        if (existingIds.has(assetId)) {
          const existingAsset = state.dataEngine.selectedAssetDetails.find(
            (a) => getIDKey(a) === assetId,
          )
          existingAsset.position = asset.position
          existingAsset.isScheduled = asset.isScheduled ?? false
          existingAsset.isPinned = asset.isPinned ?? false
          if (asset.availableFrom) {
            existingAsset.availableFrom = asset.availableFrom
          } else {
            delete existingAsset.availableFrom
          }
          if (asset.availableTo) {
            existingAsset.availableTo = asset.availableTo
          } else {
            delete existingAsset.availableTo
          }
          return existingAsset
        } else {
          return asset
        }
      })

      // Set selectedAssetDetails to the ordered updatedAssets array
      state.dataEngine.selectedAssetDetails = updatedAssets
    },
    setManualPinnedItemDetail: (state, action: PayloadAction<IPinnedItemDetailInRedux>) => {
      state.manualPinnedItems = action.payload
    },
    replaceSelectedAssetDetails: (state, action: PayloadAction<any[]>) => {
      state.dataEngine.selectedAssetDetails = action.payload
    },
    clearSelectedAssetDetails: (state) => {
      state.dataEngine.selectedAssetDetails = []
    },
    resetWidgetData: () => initialState,
  },
})
export const {
  clearSelectedAssetDetails,
  replaceSelectedAssetDetails,
  setSelectedAssetDetails,
  setWidgetDefinitionDetails,
  setWidgetDefinitionId,
  setWidgetDetailId,
  setWidgetDetails,
  resetWidgetData,
  setIsDirty,
  setIsPinAssetDirty,
  setManualPinnedItemDetail,
} = manageWidgetSlice.actions
export default manageWidgetSlice
export const manageWidgetSelector = (state: any) => state.manageWidget
export const getIDKey = (asset: any): string | undefined => {
  return asset?.contentId || asset?.assetId || asset?.containerId || asset?.widgetId || asset?.id
}
export const setIsDirtySelector = createSelector(
  [manageWidgetSelector],
  (parameters) => parameters.isDirty,
)
export const setIsPinAssetDirtySelector = createSelector(
  [manageWidgetSelector],
  (parameters) => parameters.isPinAssetDirty,
)
export const dataEngineSelector = createSelector(
  [manageWidgetSelector],
  (parameters) => parameters.dataEngine,
)
export const widgetDefinitionData = createSelector(
  [manageWidgetSelector],
  (parameters) => parameters.widgetDefinitionDetails,
)
export const widgetDetailsSelector = createSelector(
  [manageWidgetSelector],
  (parameters) => parameters.widgetDetails,
)

export const getWidgetListAPICall = createAsyncThunk(
  'manageWidget/widgetList',
  async (payload: any, { dispatch }) => {
    const requestedData: any = WIDGET_MANAGEMENT_CRUD.LISTING(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)
export const assetList = createAsyncThunk(
  'manageWidget/assetList',
  async (data: any, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal = WIDGET_MANAGEMENT_CRUD.ASSET_LISTING(data)
    const result = await mainApiService(requestedData)
    return result
  },
)

export const assetTypes = createAsyncThunk('manageWidget/assetTypes', async () => {
  const requestedData: WidgetCRUDAPIDefinitionModal = WIDGET_MANAGEMENT_CRUD.TYPES_LISTING()
  const result = await mainApiService(requestedData)
  return result
})
export const assetLayouts = createAsyncThunk(
  'manageWidget/assetLayouts',
  async (widgetType: string, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.LAYOUTS_LISTING(widgetType)
    const result = await mainApiService(requestedData)
    return result
  },
)
export const getAssetsDetailByIds = createAsyncThunk(
  'manageWidget/getAssetsDetailByIds',
  async (data: any, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_ASSETS_DETAIL_BY_IDS(data.data)
    const result: any = await mainApiService(requestedData)
    if (data.mode === 'replace') {
      dispatch(replaceSelectedAssetDetails(result?.data?.records))
    } else {
      dispatch(setSelectedAssetDetails(result?.data?.records))
    }
    return result
  },
)
export const getWidgetDefinitionDetailById = createAsyncThunk(
  'manageWidget/getWidgetDefinitionDetailById',
  async (data: string, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_WIDGET_DEFINITION_BY_ID(data)
    const result: any = await mainApiService(requestedData)
    dispatch(setWidgetDefinitionDetails({ type: 'add', value: result?.data }))
    return result
  },
)

export const getWidgetDetailById = createAsyncThunk(
  'manageWidget/getWidgetDetailById',
  async (data: string, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_WIDGET_DETAILS_BY_ID(data)
    const result: any = await mainApiService(requestedData)
    const resultData = result?.data
    if (
      resultData?.data?.engine?.toLowerCase() === DATA_ENGINES.automated ||
      resultData?.data?.engine?.toLowerCase() === DATA_ENGINES.recommendation
    ) {
      if (resultData?.data?.query && Object.keys(resultData?.data?.query)?.length > 0) {
        resultData.data.query = Object.entries(resultData.data.query).reduce(
          (acc: any, [key, value]: any) => {
            if (Array.isArray(value)) {
              value = value.map((item: any) => {
                const { parameterId } = item
                if (parameterId && typeof parameterId === 'object') {
                  return {
                    ...item,
                    parameterId: parameterId._id,
                    dataType: parameterId.dataType,
                    fieldType: parameterId.fieldType,
                  }
                }
                return item
              })
            }
            acc[key] = value
            return acc
          },
          {},
        )
      }
    }
    dispatch(setWidgetDetails({ type: 'add', value: resultData }))
    return result
  },
)
export interface WidgetCRUDAPIDefinitionModal {
  url: string
  method: string
  data: any
}
